import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import {
  type ApiError,
  addActionNameToMutationResult,
  handleZodError,
  useAxiosInstance,
} from '../../../../../../../../shared/hooks/utils';

const CreateDebtorAiSummarySchema = z.object({
  debtorId: z.string(),
});

export type CreateDebtorAiSummaryVariables = z.infer<typeof CreateDebtorAiSummarySchema>;

const DebtorAiSummarySchemaResponse = z.object({
  summary: z.object({
    analysis: z.string(),
    overallAssessment: z.string(),
  }),
});

export type DebtorAiSummaryResponse = z.infer<typeof DebtorAiSummarySchemaResponse>;

export async function createDebtorAiSummaryQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateDebtorAiSummaryVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.post(`/debtors/${variables.debtorId}/ai_summary`);

  const parseResult = DebtorAiSummarySchemaResponse.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'debtorAiSummary');
    throw parseResult.error;
  }

  return parseResult.data;
}

export function useCreateDebtorAiSummary() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<DebtorAiSummaryResponse, ApiError, CreateDebtorAiSummaryVariables>({
    mutationKey: ['createDebtorAiSummary'],
    mutationFn: data => createDebtorAiSummaryQueryFn(axiosInstance, data),
    onSuccess: _data => {
      client.invalidateQueries({
        queryKey: ['aiSummary'],
      });
    },
  });

  return addActionNameToMutationResult<
    DebtorAiSummaryResponse,
    ApiError,
    CreateDebtorAiSummaryVariables,
    'createDebtorAiSummary'
  >('createDebtorAiSummary', mutation);
}
